import axios, { AxiosResponse } from 'axios'
import qs from 'qs'

import { CONTENT_TYPE_CONVERSATION, CONTENT_TYPE_QUESTION, ContentType } from '@insided/shared/utils/contentType'

/* eslint-disable import/no-internal-modules */
import { capitalize } from '@insided/deprecated-preact-components/utils/string'
import { csrfToken } from '@insided/deprecated-preact-components/utils/csrfToken'

export async function getTopic(contentType: ContentType, privateId: number) {
  const { data } = await axios.get(`/community/api/${contentType}s/${privateId}`)
  return data
}

export async function getReply(contentType: ContentType, privateId: number, replyId: number) {
  const { data } = await axios.get(`/community/api/${contentType}s/${privateId}/replies/${replyId}`)
  return data
}

export async function getReplies(
  contentType: ContentType,
  privateId: number,
  page?: number,
  pageSize?: number,
  sort?: string
) {
  const parameters = qs.stringify({ page, pageSize, sort }, { addQueryPrefix: true })
  const { data } = await axios.get(`/community/api/${contentType}s/${privateId}/replies${parameters}`)
  return data
}

export async function replyToTopic(
  contentType: ContentType,
  privateId: number,
  content: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/reply`, {
    content: content
  })
}

export async function like(contentType: ContentType, privateId: number, likedBy: number): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/like`, {
    likedBy
  })
}

export async function unlike(contentType: ContentType, privateId: number, unlikedBy: number): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/unlike`, {
    unlikedBy
  })
}

export async function vote(contentType: ContentType, privateId: number, votedBy: number): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/vote`, {
    votedBy
  })
}

export async function unvote(contentType: ContentType, privateId: number, unvotedBy: number): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/unvote`, {
    unvotedBy
  })
}

export async function mergeVotes(
  contentType: ContentType,
  fromPrivateId: number,
  toPrivateId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${fromPrivateId}/mergeVotes`, {
    toIdeaId: toPrivateId
  })
}

export async function assignIdeaStatus(
  contentType: ContentType,
  privateId: number,
  ideaStatusId: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/assignIdeaStatus`, {
    ideaStatusId: ideaStatusId
  })
}

export async function assignModerator(
  contentType: ContentType,
  privateIds: number[],
  moderatorId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/assignModerator`, {
    ideaIds: privateIds,
    assignedTo: moderatorId
  })
}

export async function unassignModerator(contentType: ContentType, privateIds: number[]): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/unassignModerator`, {
    ideaIds: privateIds
  })
}

export async function editContent(
  contentType: ContentType,
  privateId: number,
  content: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editContent`, {
    content
  })
}

export async function changeCategory(
  privateId: number,
  contentType: ContentType,
  categoryId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/move`, {
    categoryId
  })
}

export async function editPublicLabel(
  contentType: ContentType,
  privateId: number,
  publicLabel: string
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/editPublicLabel`, {
    publicLabel
  })
}

export async function changeAuthor(
  contentType: ContentType,
  privateId: number,
  authorId: number
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/changeAuthor`, {
    authorId
  })
}

export async function toggleTrashed(
  contentType: ContentType,
  privateId: number,
  trashed: boolean
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/toggleTrashed`, {
    trashed
  })
}

export async function toggleReplyTrashed(
  contentType: ContentType,
  replyId: number,
  trashed: boolean
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/replies/${replyId}/toggleTrashed`, {
    trashed
  })
}

export async function convert(
  contentType: ContentType,
  privateId: number,
  newContentType: ContentType,
  categoryId?: number
): Promise<AxiosResponse> {
  // for question to conversation conversion and vice versa the endpoint is /convert
  // for other content type conversions the endpoint is /convertTo[ContentType]
  const exceptionTypes = [CONTENT_TYPE_QUESTION, CONTENT_TYPE_CONVERSATION]
  const endpoint =
    exceptionTypes.includes(contentType) && exceptionTypes.includes(newContentType)
      ? `/community/api/${contentType}s/${privateId}/convert`
      : `/community/api/${contentType}s/${privateId}/convertTo${capitalize(newContentType)}`

  if (categoryId) {
    return await axios.post(endpoint, { categoryId })
  }
  return await axios.post(endpoint)
}

export async function approveTopic(contentType: ContentType, privateId: number): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${privateId}/approve`)
}

export async function approveReply(contentType: ContentType, replyId: number): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/replies/${replyId}/approve`)
}

export async function pinReply(contentType: ContentType, replyId: number): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/replies/${replyId}/pin`)
}

export async function unpinReply(contentType: ContentType, replyId: number): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/replies/${replyId}/unpin`)
}

export async function moveTopicToExistingTopic(
  contentType: ContentType,
  fromTopicId: number,
  toTopicId: number,
  toTopicType: ContentType
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${fromTopicId}/moveToTopic`, {
    topicId: toTopicId,
    topicType: toTopicType
  })
}

export async function moveRepliesToExistingTopic(
  repliesContentType: ContentType,
  replyIds: string[],
  topicId: number,
  topicContentType: ContentType
): Promise<AxiosResponse> {
  return await axios.post('/community/api/replies/move', {
    repliesContentType,
    replyIds,
    topicId,
    topicContentType
  })
}

export async function promoteReply(contentType: ContentType, replyId: number, title: string, categoryId: number) {
  const response = await axios.post(`/community/api/${contentType}s/replies/${replyId}/promote`, {
    title,
    categoryId
  })
  return getTopicIdFromResponse(response)
}

export async function copyTopicToForum(
  contentType: ContentType,
  topicId: number,
  categoryIds: string[]
): Promise<AxiosResponse> {
  return await axios.post(`/community/api/${contentType}s/${topicId}/copy`, {
    categoryIds
  })
}

export async function markBestAnswer(
  contentType: ContentType,
  replyId: string | null
) {
  return await axios.post(`/community/api/${contentType}s/replies/${replyId}/answer`)
}

export async function unmarkBestAnswer(
  contentType: ContentType,
  replyId: string | null
) {
  return await axios.delete(`/community/api/${contentType}s/replies/${replyId}/answer`, {
    data: { csrfToken: csrfToken() }
  })
}

export async function getPrivateId(publicId: number) {
  const {
    data: { privateId }
  } = await axios.get(`/topic/privateId/${publicId}`)
  return privateId
}

export async function getContentType(publicId: number) {
  const {
    data: { contentType }
  } = await axios.get(`/topic/contentType?publicId=${publicId}`)
  return contentType
}

export async function getModerationLabels() {
  const { data } = await axios.get('/settings/label/getLabels')
  return data
}

export async function getIdeaStatuses() {
  const { data } = await axios.get('/community/api/ideas/ideaStatuses')
  return data.result
}

export async function getTopicByUrl(topicUrl: string) {
  const { data } = await axios.get(`/topic/checkurl?checkurl=${topicUrl}`)
  return data
}

function getTopicIdFromResponse(response: AxiosResponse) {
  return Number(response.headers.location.split('/')[3])
}
